import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogContent, FormControl, Grid, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { BindingsApi } from '@/api/domains/bindings.api';
import { ReconciliationQueryKeys } from '@/api/domains/reconciliation.query-keys';
import {
  ConfirmationDialogActionCancelButton,
  ConfirmationDialogActionProceedButton,
  ConfirmationDialogBodyCard,
  ConfirmationDialogBodyMutedText,
  ConfirmationDialogBodyText,
  DialogHeader,
  useGlobalErrorStore,
} from '@/components/dialogs';
import { CustomFormLabel, FormProvider, RHFTextField } from '@/components/form-controls';
import { ReactComponent as BindSVG } from '@/shared/assets/icons/dialog/bind.svg';
import { formatDrugFullName } from '@/shared/helpers/format.helpers';
import { useShortcut } from '@/shared/hooks/useShortcut';

import { useReconciliationOrderGuid } from '../../reconciliation-file.store';
import {
  useIsBindDialogOpen,
  useReconciliationBindingActions,
  useSelectedNomenclatureDrug,
  useSelectedUnboundDrug,
} from '../reconciliation-binding.store';

const StyledBindSVG = styled(BindSVG)(({ theme }) => ({
  width: theme.spacing(7),
  height: theme.spacing(7),
  color: theme.palette.text.secondary,
  '& > rect': {
    fill: 'transparent',
    stroke: 'transparent',
  },
}));

const StyledConfirmationDialogBodyCard = styled(ConfirmationDialogBodyCard)({
  minHeight: '102px',
  flexGrow: 2,
  gap: '4px',
}) as typeof ConfirmationDialogBodyCard;

type FormValues = {
  notes: string;
};

type BindParams = {
  bindingId: number;
  drugId: number;
  notes: string;
};

export const ReconciliationBindDialog = () => {
  const { t } = useTranslation('orders-page');

  const isOpen = useIsBindDialogOpen();
  const { closeBindDialog, resetSelectedUnboundDrug, resetSelectedNomenclatureDrug } =
    useReconciliationBindingActions();
  const selectedUnboundDrug = useSelectedUnboundDrug();
  const selectedNomenclatureDrug = useSelectedNomenclatureDrug();
  const orderGuid = useReconciliationOrderGuid();
  const queryClient = useQueryClient();
  const { setGlobalError } = useGlobalErrorStore();

  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: { notes: '' },
  });

  const { getValues, reset } = methods;

  const handleReset = () => reset();

  const handleCloseBindDialog = () => {
    closeBindDialog();
    handleReset();
  };

  const { mutate: bind, isLoading } = useMutation({
    mutationFn: ({ bindingId, drugId, notes }: BindParams) =>
      BindingsApi.updateBinding(bindingId, { drug_id: drugId, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries(ReconciliationQueryKeys.documentDetails(orderGuid));
      queryClient.invalidateQueries(ReconciliationQueryKeys.unboundBindings(orderGuid));
      resetSelectedUnboundDrug();
      resetSelectedNomenclatureDrug();
      handleCloseBindDialog();
    },
    onError: setGlobalError,
  });

  useShortcut({
    combination: 'enter',
    onPressed: () => {
      if (!isOpen || !selectedUnboundDrug || !selectedNomenclatureDrug) return;

      const { notes } = getValues();
      bind({ bindingId: selectedUnboundDrug.id, drugId: selectedNomenclatureDrug.id, notes });
    },
  });

  if (!selectedUnboundDrug || !selectedNomenclatureDrug) {
    return null;
  }

  const handleBind = (formData: FormValues) =>
    bind({ bindingId: selectedUnboundDrug.id, drugId: selectedNomenclatureDrug.id, notes: formData.notes });

  const {
    name: unboundName,
    maker_name: unboundMakerName,
    maker_country_name: unboundCountryName,
  } = selectedUnboundDrug;
  const nomenclatureName = formatDrugFullName(selectedNomenclatureDrug) ?? '';
  const { maker: nomenclatureMaker } = selectedNomenclatureDrug;

  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth="md"
      onClose={handleCloseBindDialog}
      TransitionProps={{ onExited: handleReset }}
    >
      <DialogHeader
        title={t('reconciliation.binding.dialogs.create-binding.title')}
        variant="done"
        onClose={handleCloseBindDialog}
      />

      <FormProvider methods={methods} onSubmit={handleBind} alignSelf="stretch">
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <Grid container columnSpacing={2}>
            <Grid item xs>
              <Stack height="100%">
                <Typography component="p" variant="Title/medium" color="neutral.700" sx={{ mb: 1 }}>
                  {t('reconciliation.binding.dialogs.create-binding.in-document')}
                </Typography>

                <StyledConfirmationDialogBodyCard>
                  <ConfirmationDialogBodyText variant="Title/small" color="neutral.900">
                    {unboundName}
                  </ConfirmationDialogBodyText>
                  {unboundMakerName && (
                    <ConfirmationDialogBodyMutedText color="neutral.900">
                      {unboundMakerName}
                    </ConfirmationDialogBodyMutedText>
                  )}
                  {unboundCountryName && (
                    <ConfirmationDialogBodyMutedText variant="Body/small">
                      {unboundCountryName}
                    </ConfirmationDialogBodyMutedText>
                  )}
                </StyledConfirmationDialogBodyCard>
              </Stack>
            </Grid>

            <Grid item sx={{ display: 'flex', alignItems: 'center', pt: 3 }}>
              <StyledBindSVG />
            </Grid>

            <Grid item xs>
              <Stack height="100%">
                <Typography component="p" variant="Title/medium" color="neutral.700" sx={{ mb: 1 }}>
                  {t('reconciliation.binding.dialogs.create-binding.in-nomenclature')}
                </Typography>

                <StyledConfirmationDialogBodyCard>
                  <ConfirmationDialogBodyText variant="Title/small" color="neutral.900">
                    {nomenclatureName}
                  </ConfirmationDialogBodyText>
                  {nomenclatureMaker?.name && (
                    <ConfirmationDialogBodyMutedText color="neutral.900">
                      {nomenclatureMaker.name}
                    </ConfirmationDialogBodyMutedText>
                  )}
                  {nomenclatureMaker?.country_name && (
                    <ConfirmationDialogBodyMutedText variant="Body/small">
                      {nomenclatureMaker.country_name}
                    </ConfirmationDialogBodyMutedText>
                  )}
                </StyledConfirmationDialogBodyCard>
              </Stack>
            </Grid>
          </Grid>

          <FormControl component={Stack} spacing={1} sx={{ mb: 1, mt: 2 }} fullWidth>
            <CustomFormLabel htmlFor="notes">
              {t('reconciliation.binding.dialogs.create-binding.notes')}
            </CustomFormLabel>
            <RHFTextField name="notes" rows={1} variant="filled" />
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ py: 2, px: 3 }}>
          <ConfirmationDialogActionCancelButton fullWidth={false} onClick={handleCloseBindDialog}>
            {t('reconciliation.binding.dialogs.create-binding.cancel')}
          </ConfirmationDialogActionCancelButton>
          <ConfirmationDialogActionProceedButton autoFocus type="submit" loading={isLoading} fullWidth={false}>
            {t('reconciliation.binding.dialogs.create-binding.create')}
          </ConfirmationDialogActionProceedButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};
